import * as ace from 'brace';
import 'brace/mode/text';
import 'brace/theme/github';
import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, Directive, Optional, Inject, Input, Output, Component, ViewEncapsulation, HostBinding, ViewChild, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';

const ACE_CONFIG = new InjectionToken('ACE_CONFIG');
const AceEditorEvents = [
    'blur',
    'focus',
    'copy',
    'paste',
    'change',
    'changeSession'
];
const AceSelectionEvents = [
    'changeCursor',
    'changeSelection'
];
class AceConfig {
    constructor(config = {}) {
        this.assign(config);
    }
    assign(config = {}, target) {
        target = target || this;
        for (const key in config) {
            if (config[key] && !Array.isArray(config[key]) && typeof config[key] === 'object') {
                target[key] = {};
                this.assign(config[key], target[key]);
            }
            else {
                target[key] = config[key];
            }
        }
    }
}

class AceDirective {
    constructor(zone, elementRef, differs, defaults) {
        this.zone = zone;
        this.elementRef = elementRef;
        this.differs = differs;
        this.defaults = defaults;
        this.instance = null;
        this.instanceEventListeners = [];
        this.selectionEventListeners = [];
        this.configDiff = null;
        this.disabled = false;
        this.blur = new EventEmitter();
        this.focus = new EventEmitter();
        this.copy = new EventEmitter();
        this.paste = new EventEmitter();
        this.change = new EventEmitter();
        this.changeCursor = new EventEmitter();
        this.changeSession = new EventEmitter();
        this.changeSelection = new EventEmitter();
    }
    ngOnInit() {
        const params = new AceConfig(this.defaults);
        params.assign(this.config); // Custom configuration
        if (this.disabled) {
            params.readOnly = true;
            params.highlightActiveLine = false;
        }
        params.mode = 'ace/mode/' + (params.mode || 'text');
        params.theme = 'ace/theme/' + (params.theme || 'github');
        this.zone.runOutsideAngular(() => {
            this.instance = ace.edit(this.elementRef.nativeElement);
            this.instance.$blockScrolling = Infinity;
            this.instance.setOptions(params);
        });
        // Add native Ace event handling
        AceEditorEvents.forEach((eventName) => {
            if (this.instance) {
                const callback = (...args) => {
                    if (args.length === 1) {
                        args = args[0];
                    }
                    if (this[eventName]) {
                        this.zone.run(() => {
                            if (this[eventName].observers.length) {
                                this[eventName].emit(args);
                            }
                        });
                    }
                };
                this.instance.on(eventName, callback);
                this.instanceEventListeners.push({ eventName, callback });
            }
        });
        // Add native Ace selection event handling
        AceSelectionEvents.forEach((eventName) => {
            if (this.instance) {
                const callback = (...args) => {
                    if (args.length === 1) {
                        args = args[0];
                    }
                    if (this[eventName]) {
                        if (this[eventName].observers.length) {
                            this[eventName].emit(args);
                        }
                    }
                };
                this.instance.selection.on(eventName, callback);
                this.selectionEventListeners.push({ eventName, callback });
            }
        });
        if (!this.configDiff) {
            this.configDiff = this.differs.find(this.config || {}).create();
            this.configDiff.diff(this.config || {});
        }
    }
    ngDoCheck() {
        if (this.configDiff) {
            const changes = this.configDiff.diff(this.config || {});
            if (changes) {
                this.ngOnDestroy();
                this.ngOnInit();
            }
        }
    }
    ngOnDestroy() {
        if (this.instance) {
            if (this.instance.isFocused()) {
                this.blur.emit();
            }
            this.instanceEventListeners.forEach((el) => {
                this.instance.off(el.eventName, el.callback);
            });
            this.instanceEventListeners = [];
            this.selectionEventListeners.forEach((el) => {
                this.instance.selection.off(el.eventName, el.callback);
            });
            this.selectionEventListeners = [];
            delete this.instance;
            this.instance = null;
        }
    }
    ngOnChanges(changes) {
        if (changes['disabled']) {
            if (changes['disabled'].currentValue !== changes['disabled'].previousValue) {
                this.zone.runOutsideAngular(() => {
                    if (this.instance) {
                        const params = new AceConfig(this.defaults);
                        params.assign(this.config); // Custom configuration
                        this.instance.clearSelection();
                        const hlActive = (params.highlightActiveLine == null) ? true : false;
                        this.instance.setHighlightActiveLine(this.disabled ? false : hlActive);
                        this.instance.setReadOnly(this.disabled ? true : (params.readOnly || false));
                    }
                });
            }
        }
    }
    ace() {
        return this.instance;
    }
    clear() {
        if (this.instance) {
            this.instance.setValue('');
            this.instance.clearSelection();
        }
    }
    getValue() {
        if (this.instance) {
            return this.instance.getValue();
        }
    }
    setValue(value, cursorPos) {
        if (this.instance) {
            this.instance.setValue(value || '', cursorPos);
        }
    }
}
AceDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceDirective, deps: [{ token: i0.NgZone }, { token: i0.ElementRef }, { token: i0.KeyValueDiffers }, { token: ACE_CONFIG, optional: true }], target: i0.ɵɵFactoryTarget.Directive });
AceDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.0", type: AceDirective, selector: "[ace]", inputs: { disabled: "disabled", config: ["ace", "config"] }, outputs: { blur: "blur", focus: "focus", copy: "copy", paste: "paste", change: "change", changeCursor: "changeCursor", changeSession: "changeSession", changeSelection: "changeSelection" }, exportAs: ["ngxAce"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ace]',
                    exportAs: 'ngxAce'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ElementRef }, { type: i0.KeyValueDiffers }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [ACE_CONFIG]
                }] }]; }, propDecorators: { disabled: [{
                type: Input
            }], config: [{
                type: Input,
                args: ['ace']
            }], blur: [{
                type: Output
            }], focus: [{
                type: Output
            }], copy: [{
                type: Output
            }], paste: [{
                type: Output
            }], change: [{
                type: Output
            }], changeCursor: [{
                type: Output
            }], changeSession: [{
                type: Output
            }], changeSelection: [{
                type: Output
            }] } });

class AceComponent {
    constructor() {
        this.content = '';
        this.disabled = false;
        this.mode = '';
        this.theme = '';
        this.useAceClass = true;
        this.blur = new EventEmitter();
        this.focus = new EventEmitter();
        this.copy = new EventEmitter();
        this.paste = new EventEmitter();
        this.change = new EventEmitter();
        this.valueChange = new EventEmitter();
        this.changeCursor = new EventEmitter();
        this.changeSession = new EventEmitter();
        this.changeSelection = new EventEmitter();
    }
    get value() {
        return this.content;
    }
    set value(value) {
        this.setContent(value);
    }
    ngAfterViewInit() {
        if (this.content != null) {
            this.setContent(this.content, true);
        }
    }
    setContent(value, force) {
        if (force || value !== this.content) {
            if (this.directiveRef) {
                this.directiveRef.setValue(value, 1);
            }
            this.content = value;
        }
    }
    getConfig() {
        this.config = this.config || {};
        this.config.mode = this.mode || this.config.mode;
        this.config.theme = this.theme || this.config.theme;
        return this.config;
    }
    onContentChange(event) {
        if (this.directiveRef) {
            this.change.emit(event);
            this.content = this.directiveRef.getValue() || '';
            this.valueChange.emit(this.value);
        }
    }
}
AceComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
AceComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.0", type: AceComponent, selector: "ace", inputs: { value: "value", disabled: "disabled", mode: "mode", theme: "theme", config: "config", useAceClass: "useAceClass" }, outputs: { blur: "blur", focus: "focus", copy: "copy", paste: "paste", change: "change", valueChange: "valueChange", changeCursor: "changeCursor", changeSession: "changeSession", changeSelection: "changeSelection" }, host: { properties: { "class.ace": "this.useAceClass" } }, viewQueries: [{ propertyName: "directiveRef", first: true, predicate: AceDirective, descendants: true, static: true }], exportAs: ["ngxAce"], ngImport: i0, template: "<div [ace]=\"getConfig()\" [disabled]=\"disabled\" (blur)=\"blur.emit($event)\" (focus)=\"focus.emit($event)\" (copy)=\"copy.emit($event)\" (paste)=\"paste.emit($event)\" (change)=\"onContentChange($event)\" (changeCursor)=\"changeCursor.emit($event)\" (changeSession)=\"changeSession.emit($event)\" (changeSelection)=\"changeSelection.emit($event)\">\n</div>\n", styles: [".ace,.ace>.ace_editor{display:block;width:100%;height:100%}.ace[fxflex]{display:flex;flex-direction:inherit;align-items:inherit;min-width:0;min-height:0}.ace[fxflex]>.ace_editor{flex:1 1 auto;min-width:0;min-height:0;-webkit-box-flex:1}\n"], dependencies: [{ kind: "directive", type: AceDirective, selector: "[ace]", inputs: ["disabled", "ace"], outputs: ["blur", "focus", "copy", "paste", "change", "changeCursor", "changeSession", "changeSelection"], exportAs: ["ngxAce"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ace', exportAs: 'ngxAce', encapsulation: ViewEncapsulation.None, template: "<div [ace]=\"getConfig()\" [disabled]=\"disabled\" (blur)=\"blur.emit($event)\" (focus)=\"focus.emit($event)\" (copy)=\"copy.emit($event)\" (paste)=\"paste.emit($event)\" (change)=\"onContentChange($event)\" (changeCursor)=\"changeCursor.emit($event)\" (changeSession)=\"changeSession.emit($event)\" (changeSelection)=\"changeSelection.emit($event)\">\n</div>\n", styles: [".ace,.ace>.ace_editor{display:block;width:100%;height:100%}.ace[fxflex]{display:flex;flex-direction:inherit;align-items:inherit;min-width:0;min-height:0}.ace[fxflex]>.ace_editor{flex:1 1 auto;min-width:0;min-height:0;-webkit-box-flex:1}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], mode: [{
                type: Input
            }], theme: [{
                type: Input
            }], config: [{
                type: Input
            }], useAceClass: [{
                type: HostBinding,
                args: ['class.ace']
            }, {
                type: Input
            }], blur: [{
                type: Output
            }], focus: [{
                type: Output
            }], copy: [{
                type: Output
            }], paste: [{
                type: Output
            }], change: [{
                type: Output
            }], valueChange: [{
                type: Output
            }], changeCursor: [{
                type: Output
            }], changeSession: [{
                type: Output
            }], changeSelection: [{
                type: Output
            }], directiveRef: [{
                type: ViewChild,
                args: [AceDirective, { static: true }]
            }] } });

class AceModule {
}
AceModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AceModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.0", ngImport: i0, type: AceModule, declarations: [AceComponent, AceDirective], imports: [CommonModule], exports: [CommonModule, AceComponent, AceDirective] });
AceModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceModule, imports: [CommonModule, CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.0", ngImport: i0, type: AceModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AceComponent, AceDirective],
                    imports: [CommonModule],
                    exports: [CommonModule, AceComponent, AceDirective],
                    providers: []
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ACE_CONFIG, AceComponent, AceConfig, AceDirective, AceModule };
